import { type ProspectDto, useSalesGetSalesFolks } from "@crowdcoursing/api/openapi";
import { Person } from "@mui/icons-material";
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";

import { useSelector } from "~hooks/useTypedRedux";
import { SalesOwnerAvatar } from "~ui/molecules/SalesOwnerAvatar/SalesOwnerAvatar";

const tAssignToMe = "Assign to me";
const tUnassignMe = "Unassign me";
const tNoAssignments = "No assignments yet";
const tOwner = "Owner";
const tAssignUser = "Assign user";
const tSelectUser = "Select user";
const tSalesManager = "Sales Manager";

interface AssignmentsCardProps {
  prospect: ProspectDto;
  onAssignToMe: () => Promise<void>;
  onUnassignMe: () => Promise<void>;
  onAssignUser?: (userId: string) => Promise<void>;
  onUnassignUser?: (userId: string) => Promise<void>;
  roles?: string[];
}

export const AssignmentsCard = ({
  prospect,
  onAssignToMe,
  onUnassignMe,
  onAssignUser,
  onUnassignUser,
  roles = [],
}: AssignmentsCardProps) => {
  const currentUserId = useSelector((state) => state.user?.id);
  const isOwner = prospect.ownerUserId === currentUserId;

  // Check if user has Sales role if not explicitly provided
  const isSalesManager = roles.includes("Sales") && roles.includes("Admin");

  // Fetch sales personnel
  const { data: salesPersonnel = [] } = useSalesGetSalesFolks({});

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Assignment Status */}
      {prospect.assignments?.length ? (
        <Stack spacing={2}>
          {/* Owner */}
          {prospect.ownerUserId && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {tOwner}
              </Typography>
              <SalesOwnerAvatar
                userId={prospect.ownerUserId}
                firstName={prospect.ownerFirstName}
                lastName={prospect.ownerLastName}
                size="medium"
              />
            </Box>
          )}
        </Stack>
      ) : (
        <Typography color="text.secondary">{tNoAssignments}</Typography>
      )}

      {/* Assign/Unassign Button */}
      {isOwner ? (
        <Button onClick={onUnassignMe} startIcon={<Person />} variant="outlined" color="error">
          {tUnassignMe}
        </Button>
      ) : (
        <Button onClick={onAssignToMe} startIcon={<Person />} variant="outlined">
          {tAssignToMe}
        </Button>
      )}

      {/* Sales Manager User Assignment Section */}
      {isSalesManager && onAssignUser && onUnassignUser && (
        <Box mt={2}>
          <Typography variant="subtitle1" color="primary" gutterBottom>
            {tSalesManager}
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            {tAssignUser}
          </Typography>

          {/* User Selection Dropdown - Always present */}
          <FormControl fullWidth size="small">
            <InputLabel id="assign-user-label">{tSelectUser}</InputLabel>
            <Select
              labelId="assign-user-label"
              label={tSelectUser}
              value=""
              onChange={(e) => {
                const userId = e.target.value;
                if (userId && onAssignUser) {
                  onAssignUser(userId);
                }
              }}
            >
              {salesPersonnel.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                    <SalesOwnerAvatar
                      userId={user.id}
                      firstName={user.firstName}
                      lastName={user.lastName}
                      showName={true}
                      size="small"
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};
